<template>
  <div class="allowed-times">
    <h3 class="times-title">{{ $t("Time slots") }}</h3>

    <p v-if="!selectedDate" class="empty-state">
      {{ $t("No appointments available") }}
    </p>

    <p v-else-if="selectedDate && !availableSlots.length" class="empty-state">
      {{ $t("No appointments available") }}
    </p>

    <div v-else class="time-grid">
      <button
        v-for="(slot, index) in availableSlots"
        :key="`${slot.time}-${index}`"
        class="time-slot"
        :class="{
          'time-slot--active': selectedTimeIndex === index,
          'time-slot--disabled': !slot.available,
        }"
        :disabled="!slot.available"
        type="button"
        @click="selectTime(index)"
      >
        {{ getSlotLabel(slot, index) }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "allowedTimes",
  props: {
    availableSlots: {
      type: Array,
      default: () => {
        return [];
      },
    },
    selectedDate: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      selectedTimeIndex: null,
    };
  },
  watch: {
    selectedDate() {
      this.selectedTimeIndex = null;
    },
  },
  methods: {
    selectTime(index) {
      if (!this.availableSlots[index] || !this.availableSlots[index].available)
        return;

      this.selectedTimeIndex = index;
      this.$emit("changeTime", this.availableSlots[index].time);
    },
    translateTime(text) {
      return text.replace(/am/gi, this.$t("am")).replace(/pm/gi, this.$t("pm"));
    },
    getSlotLabel(slot, index) {
      const nextSlot = this.availableSlots[index + 1];
      if (!nextSlot) return this.translateTime(slot.time);

      return `${this.translateTime(slot.time)} - ${this.translateTime(
        nextSlot.time
      )}`;
    },
  },
};
</script>

<style scoped lang="scss">
.allowed-times {
  height: 100%;
  padding: 24px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
}

.times-title {
  margin: 0 0 18px;
  color: var(--main-color);
  font-size: 18px;
  font-weight: 800;
}

.empty-state {
  margin: 0;
  padding: 72px 0;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
}

.time-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
  min-width: 0;
}

.time-slot {
  width: 100%;
  max-width: 100%;
  min-height: 48px;
  padding: 10px 12px;
  border: 1px solid rgba(42, 46, 112, 0.08);
  border-radius: 16px;
  color: var(--main-color);
  font-size: 13px;
  font-weight: 700;
  background: linear-gradient(180deg, #ffffff, #f8faff);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease,
    transform 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
}

.time-slot:hover:not(:disabled) {
  border-color: rgba(42, 46, 112, 0.2);
  background: linear-gradient(180deg, #ffffff, #eef2ff);
  box-shadow: 0 12px 26px rgba(42, 46, 112, 0.12);
  transform: translateY(-1px);
}

.time-slot:focus-visible:not(:disabled) {
  outline: none;
  border-color: rgba(42, 46, 112, 0.28);
  box-shadow: 0 0 0 3px rgba(42, 46, 112, 0.12),
    0 12px 26px rgba(42, 46, 112, 0.12);
}

.time-slot--active {
  color: #fff;
  background: linear-gradient(
    180deg,
    var(--main-color),
    var(--secondary-color)
  );
  border-color: rgba(255, 255, 255, 0.18);
  box-shadow: 0 14px 26px rgba(15, 23, 42, 0.14);
}

.time-slot--active:hover:not(:disabled) {
  background: linear-gradient(
    180deg,
    var(--secondary-color),
    var(--main-color)
  );
  border-color: rgba(255, 255, 255, 0.28);
  box-shadow: 0 18px 30px rgba(15, 23, 42, 0.18);
  transform: translateY(-1px) scale(1.01);
}

.time-slot--active:focus-visible:not(:disabled) {
  border-color: rgba(255, 255, 255, 0.32);
  box-shadow: 0 0 0 3px rgba(42, 46, 112, 0.18),
    0 18px 30px rgba(15, 23, 42, 0.18);
}

.time-slot--disabled {
  cursor: not-allowed;
  color: #a8b0c5;
  background: #f3f4f6;
  border-color: rgba(148, 163, 184, 0.12);
  box-shadow: none;
  opacity: 1;
}

@media (max-width: 959px) {
  .allowed-times {
    padding: 18px 16px 22px;
  }

  .time-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 420px) {
  .time-grid {
    grid-template-columns: 1fr;
  }
}
</style>
